import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8">
            <FiArrowLeft className="mr-2" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass-card p-8 md:p-12 rounded-2xl"
          >
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-8">Cookie Policy</h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg">Last Updated: June 1, 2025</p>
              
              <h2>1. Introduction</h2>
              <p>
                This Cookie Policy explains how xdimension AI Corp. ("xdimension," "we," "our," or "us") uses cookies and similar 
                technologies to recognize you when you visit our website at xdimension.ai ("Website"). It explains what these technologies 
                are and why we use them, as well as your rights to control our use of them.
              </p>

              <h2>2. What Are Cookies?</h2>
              <p>
                Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are 
                widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide 
                reporting information.
              </p>
              <p>
                Cookies set by the website owner (in this case, xdimension) are called "first-party cookies." Cookies set by parties 
                other than the website owner are called "third-party cookies." Third-party cookies enable third-party features or 
                functionality to be provided on or through the website (e.g., advertising, interactive content, and analytics). 
                The parties that set these third-party cookies can recognize your computer both when it visits the website in question 
                and also when it visits certain other websites.
              </p>

              <h2>3. Why Do We Use Cookies?</h2>
              <p>
                We use first-party and third-party cookies for several reasons. Some cookies are required for technical reasons in order 
                for our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable 
                us to track and target the interests of our users to enhance the experience on our Website. Third parties serve cookies 
                through our Website for advertising, analytics, and other purposes.
              </p>

              <h2>4. Types of Cookies We Use</h2>
              <h3>4.1 Essential Cookies</h3>
              <p>
                These cookies are strictly necessary to provide you with services available through our Website and to use some of its 
                features, such as access to secure areas. Because these cookies are strictly necessary to deliver the Website, you cannot 
                refuse them without impacting how our Website functions.
              </p>

              <h3>4.2 Performance and Functionality Cookies</h3>
              <p>
                These cookies are used to enhance the performance and functionality of our Website but are non-essential to their use. 
                However, without these cookies, certain functionality may become unavailable.
              </p>

              <h3>4.3 Analytics and Customization Cookies</h3>
              <p>
                These cookies collect information that is used either in aggregate form to help us understand how our Website is being used 
                or how effective our marketing campaigns are, or to help us customize our Website for you. We use the following analytics cookies:
              </p>
              <ul>
                <li>
                  <strong>Google Analytics:</strong> We use Google Analytics to collect information about how visitors use our Website. 
                  These cookies collect information in an anonymous form, including the number of visitors to the Website, where visitors 
                  have come to the Website from, and the pages they visited. For more information about Google Analytics' privacy practices, 
                  please visit <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600">Google's Privacy Policy</a>.
                </li>
              </ul>

              <h3>4.4 Advertising Cookies</h3>
              <p>
                These cookies are used to make advertising messages more relevant to you. They perform functions like preventing the same 
                ad from continuously reappearing, ensuring that ads are properly displayed for advertisers, and in some cases selecting 
                advertisements that are based on your interests.
              </p>

              <h3>4.5 Social Media Cookies</h3>
              <p>
                These cookies are used to enable you to share pages and content that you find interesting on our Website through third-party 
                social networking and other websites. These cookies may also be used for advertising purposes.
              </p>

              <h2>5. Your Choices Regarding Cookies</h2>
              <p>
                If you prefer to avoid the use of cookies on the Website, first you must disable the use of cookies in your browser and then 
                delete the cookies saved in your browser associated with this website. You may use this option for preventing the use of cookies 
                at any time.
              </p>
              <p>
                If you do not accept our cookies, you may experience some inconvenience in your use of our Website, and some features may not 
                function properly.
              </p>
              <p>
                If you'd like to delete cookies or instruct your web browser to delete or refuse cookies, please visit the help pages of your 
                web browser.
              </p>
              <ul>
                <li>
                  For Chrome: <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary-600">
                    https://support.google.com/chrome/answer/95647
                  </a>
                </li>
                <li>
                  For Firefox: <a href="https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored" target="_blank" rel="noopener noreferrer" className="text-primary-600">
                    https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored
                  </a>
                </li>
                <li>
                  For Safari: <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary-600">
                    https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac
                  </a>
                </li>
                <li>
                  For Edge: <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary-600">
                    https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09
                  </a>
                </li>
              </ul>

              <h2>6. Cookie Preferences</h2>
              <p>
                You can exercise your preferences in relation to cookies served on our Website by using our cookie consent manager, 
                which appears when you first visit our Website or when we update our cookie policy.
              </p>

              <h2>7. Changes to This Cookie Policy</h2>
              <p>
                We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for 
                other operational, legal, or regulatory reasons. Please therefore re-visit this Cookie Policy regularly to stay informed 
                about our use of cookies and related technologies.
              </p>
              <p>
                The date at the top of this Cookie Policy indicates when it was last updated.
              </p>

              <h2>8. Contact Us</h2>
              <p>
                If you have any questions about our use of cookies or other technologies, please contact us at:
              </p>
              <p>
                xdimension AI<br />
                Website: xdimension.ai<br />
                Email: privacy@xdimension.ai
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;